import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField, Select } from '@repo/design-system-kit';
import { SelectCountry } from '@repo/shared-kit/components';
import cx from 'clsx';
import { IbanField } from '../../form-fields/iban-field';
import { useDetailsData } from './internal';
import styles from './details.module.css';

interface DetailsProps {
  name: string;
  email: string;
  address?: string;
  readonlyAddress?: string;
  zipcode?: string;
  readonlyZipcode?: string;
  city?: string;
  readonlyCity?: string;
  country?: string;
  readonlyCountry?: string;
  province?: string;
  iban?: string;
  onUpdate: (field: string, value: string) => void;
  addressErrors?: { message: string }[];
  zipcodeErrors?: { message: string }[];
  cityErrors?: { message: string }[];
  countryErrors?: { message: string }[];
  ibanErrors?: { message: string }[];
  provinceErrors?: { message: string }[];
}

export function Details({
  name,
  email,
  address,
  readonlyAddress,
  zipcode,
  readonlyZipcode,
  city,
  readonlyCity,
  country,
  readonlyCountry,
  province,
  iban,
  onUpdate,
  addressErrors,
  zipcodeErrors,
  cityErrors,
  countryErrors,
  ibanErrors,
  provinceErrors,
}: DetailsProps) {
  const [localCountry, setLocalCountry] = useState(country);
  const [localProvince, setLocalProvince] = useState(province);
  const { formatMessage } = useIntl();

  const { isReadOnly, getValue, getErrorMessage, provinceOptions, errors } = useDetailsData({
    address,
    readonlyAddress,
    zipcode,
    readonlyZipcode,
    city,
    readonlyCity,
    readonlyCountry,
    addressErrors,
    zipcodeErrors,
    cityErrors,
    countryErrors,
    ibanErrors,
    provinceErrors,
  });

  const selectedCountry = readonlyCountry ?? localCountry;

  const handleUpdate = useCallback(
    (field: string, value: string) => {
      onUpdate(field, value);

      if (field === 'country') {
        setLocalCountry(value);
      }
    },
    [onUpdate]
  );

  const handleProvinceChange = useCallback(
    (key: string) => {
      onUpdate('province', key);
      setLocalProvince(key);
    },
    [onUpdate]
  );

  return (
    <div data-testid="sdd-payment-link-details">
      <h3 className="title-3 mb-16" data-testid="details-title">
        <FormattedMessage id="sdd-collections.payment-link-page.details" />
      </h3>

      <TextField
        className={styles.row}
        data-testid="name"
        isReadOnly
        label={formatMessage({ id: 'labels.name' })}
        placeholder={formatMessage({ id: 'labels.name' })}
        type="text"
        value={name}
      />

      <TextField
        className={styles.row}
        data-testid="email"
        isReadOnly
        label={formatMessage({ id: 'labels.email' })}
        placeholder={formatMessage({ id: 'labels.email' })}
        type="email"
        value={email}
      />

      <TextField
        className={styles.row}
        data-testid="address"
        errorMessage={errors.address ? getErrorMessage('address', errors.address) : undefined}
        isInvalid={Boolean(errors.address)}
        isReadOnly={isReadOnly('readonlyAddress')}
        label={formatMessage({ id: 'labels.address' })}
        onChange={value => {
          handleUpdate('address', value);
        }}
        placeholder={formatMessage({ id: 'placeholders.fake-address' })}
        type="text"
        value={getValue('address', readonlyAddress, localCountry)}
      />

      <div className={cx(styles.row, styles.zipcodeCity)}>
        <TextField
          data-testid="zipcode"
          errorMessage={errors.zipcode ? getErrorMessage('zipcode', errors.zipcode) : undefined}
          isInvalid={Boolean(errors.zipcode)}
          isReadOnly={isReadOnly('readonlyZipcode')}
          label={formatMessage({ id: 'labels.zipcode' })}
          onChange={value => {
            handleUpdate('zipcode', value);
          }}
          placeholder={formatMessage({ id: 'placeholders.fake-zipcode' })}
          type="text"
          value={getValue('zipcode', readonlyZipcode, localCountry)}
        />

        <TextField
          data-testid="city"
          errorMessage={errors.city ? getErrorMessage('city', errors.city) : undefined}
          isInvalid={Boolean(errors.city)}
          isReadOnly={isReadOnly('readonlyCity')}
          label={formatMessage({ id: 'labels.city' })}
          onChange={value => {
            handleUpdate('city', value);
          }}
          placeholder={formatMessage({ id: 'placeholders.fake-city' })}
          type="text"
          value={getValue('city', readonlyCity, localCountry)}
        />
      </div>

      {selectedCountry === 'IT' && (
        <Select
          className="mb-16"
          data-testid="province"
          errorMessage={errors.province ? getErrorMessage('province', errors.province) : undefined}
          isInvalid={Boolean(errors.province)}
          label={formatMessage({ id: 'sdd-collections.client-creation.province' })}
          onSelectionChange={key => {
            handleProvinceChange(String(key));
          }}
          placeholder={formatMessage({
            id: 'receivable-invoices.customer-creation.province.placeholder',
          })}
          selectedKey={localProvince}
        >
          {provinceOptions.map((option: { key: string; label: string }) => (
            <Select.Option id={option.key} key={option.key}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      )}

      <SelectCountry
        className={styles.row}
        data-testid="country"
        errorMessage={errors.country ? getErrorMessage('country', errors.country) : undefined}
        isInvalid={Boolean(errors.country)}
        isReadOnly={isReadOnly('readonlyCountry')}
        label={formatMessage({ id: 'labels.country' })}
        onSelectionChange={value => {
          handleUpdate('country', String(value));
        }}
        placeholder={formatMessage({ id: 'placeholders.country' })}
        selectedKey={selectedCountry}
      />

      <IbanField
        className={styles.row}
        data-testid="iban"
        error={errors.iban ? getErrorMessage('iban', errors.iban) : undefined}
        onUpdate={value => {
          handleUpdate('iban', value);
        }}
        value={iban}
      />
    </div>
  );
}
